import { useEffect, useRef } from 'react';

/** Keep keyboard focus inside an open dialog and restore its trigger on close. */
export function useDialogFocus(isOpen: boolean, onClose: () => void) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef(onClose);
    useEffect(() => {
        closeRef.current = onClose;
    }, [onClose]);
    useEffect(() => {
        if (!isOpen || !dialogRef.current) return;
        const dialog = dialogRef.current;
        const trigger = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const focusable = () =>
            Array.from(
                dialog.querySelectorAll<HTMLElement>(
                    'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]'
                )
            ).filter(element => element.tabIndex >= 0 && !element.closest('[hidden], [inert]'));
        (focusable()[0] ?? dialog).focus();
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                closeRef.current();
            } else if (event.key === 'Tab') {
                const items = focusable();
                const first = items[0];
                const last = items[items.length - 1];
                if (!first) {
                    event.preventDefault();
                    dialog.focus();
                } else if (
                    !dialog.contains(document.activeElement) ||
                    (event.shiftKey && document.activeElement === first)
                ) {
                    event.preventDefault();
                    (event.shiftKey ? last : first).focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        const containFocus = (event: FocusEvent) => {
            if (!dialog.contains(event.target as Node)) (focusable()[0] ?? dialog).focus();
        };
        document.addEventListener('keydown', handleKey, true);
        document.addEventListener('focusin', containFocus);
        return () => {
            document.removeEventListener('keydown', handleKey, true);
            document.removeEventListener('focusin', containFocus);
            document.body.style.overflow = previousOverflow;
            if (trigger?.isConnected) trigger.focus();
        };
    }, [isOpen]);
    return dialogRef;
}
